import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes } from "react-icons/fa";

/* ================= STYLES ================= */

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const FormContainer = styled(motion.div)`
  background: #ffffff;
  padding: 30px;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    border-color: #0a84ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(10, 132, 255, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px;
  margin-bottom: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    border-color: #0a84ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(10, 132, 255, 0.1);
  }
`;

const Label = styled.label`
  font-weight: 600;
  margin-bottom: 6px;
  display: block;
  font-size: 14px;
`;

const Button = styled.button`
  background: ${(props) =>
    props.secondary ? "#aaa" : "linear-gradient(135deg, #0a84ff, #0056d2)"};
  color: #fff;
  border: none;
  padding: 12px;
  width: 100%;
  border-radius: 30px;
  font-weight: bold;
  font-size: 0.95rem;
  cursor: pointer;
  margin-top: 8px;

  &:hover {
    opacity: 0.9;
  }
`;

/* ================= COMPONENT ================= */

export default function ProposalForm({ open, setOpen }) {

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    budget: "",
    platform: [],
    demoRequest: "",
    location: "",
    timeToCall: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => {
        const updated = checked
          ? [...prev.platform, value]
          : prev.platform.filter((p) => p !== value);
        return { ...prev, platform: updated };
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponseMessage("");

    try {
      const response = await fetch("https://api.berrybloomer.com/webapp/queries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setResponseMessage("✅ Query submitted successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          budget: "",
          platform: [],
          demoRequest: "",
          location: "",
          timeToCall: "",
          message: "",
        });
      } else {
        setResponseMessage(result.message || "❌ Submission failed.");
      }
    } catch (err) {
      setResponseMessage("❌ Server error. Try later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          >
            <FormContainer
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2
                style={{
                  textAlign: "center",
                  marginBottom: "20px",
                  color: "#0a84ff",
                }}
              >
                Get a Consultation
              </h2>

              <form onSubmit={handleSubmit}>
                <Label>Your Name</Label>
                <Input name="name" value={formData.name} onChange={handleChange} required />

                <Label>Email</Label>
                <Input type="email" name="email" value={formData.email} onChange={handleChange} required />

                <Label>Phone Number</Label>
                <Input name="phone" value={formData.phone} onChange={handleChange} required />

                <Label>Budget</Label>
                <Input name="budget" value={formData.budget} onChange={handleChange} required />

                <Label>Platform Required</Label>
                {["Android App", "iOS App", "Web App"].map((p) => (
                  <label key={p} style={{ display: "block", marginBottom: 6 }}>
                    <input
                      type="checkbox"
                      value={p}
                      checked={formData.platform.includes(p)}
                      onChange={handleChange}
                    />{" "}
                    {p}
                  </label>
                ))}

                <Label>Requesting a Demo</Label>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ marginRight: 15 }}>
                    <input
                      type="radio"
                      name="demoRequest"
                      value="Yes"
                      checked={formData.demoRequest === "Yes"}
                      onChange={handleChange}
                      required
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="demoRequest"
                      value="No"
                      checked={formData.demoRequest === "No"}
                      onChange={handleChange}
                      required
                    />{" "}
                    No
                  </label>
                </div>

                <Label>Location</Label>
                <Input name="location" value={formData.location} onChange={handleChange} required />

                <Label>Suitable Time to Call</Label>
                <Input name="timeToCall" value={formData.timeToCall} onChange={handleChange} required />

                <Label>Message (optional)</Label>
                <Textarea name="message" rows={4} value={formData.message} onChange={handleChange} />

                <Button type="submit" disabled={loading}>
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              </form>

              {responseMessage && (
                <p style={{ marginTop: 12, textAlign: "center" }}>
                  {responseMessage}
                </p>
              )}

              <Button secondary onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </FormContainer>
          </Overlay>
        )}
      </AnimatePresence>
    </>
  );
}